import React, { useMemo } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, Label, LabelList, XAxis, YAxis } from 'recharts';
import tailwindConfig from 'stylesheets/tailwind.config';
import voca from 'voca';

import { useInfiniteProjectsAnalyticsProjectsReportsTopRisksReportsDashboardReportQuery as useInfiniteTopRisksReportsDashboardReportQuery } from 'components/hooks/projectsAnalyticsProjectsReportsTopRisksReportsDashboardReportQueries';
import { useProjectsAnalyticsTopRisksReportQuery as useTopRisksReportQuery } from 'components/hooks/projectsAnalyticsTopRisksReportQueries';

import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import Card from 'components/projectsAnalytics/components/Card';
import NoDataMessage from 'components/projectsAnalytics/components/NoDataMessage';
import ProjectsModal from 'components/projectsAnalytics/components/ProjectsModal';
import ReportHeader from 'components/projectsAnalytics/components/ReportHeader';
import {
  filtersFormatter,
  filtersListFormatter,
} from 'components/projectsAnalytics/helpers/reportHeader';
import useFilters from 'components/projectsAnalytics/hooks/useFilters';
import useKeyHighlight from 'components/projectsAnalytics/hooks/useKeyHighlight';
import useProjectsModal from 'components/projectsAnalytics/hooks/useProjectsModal';

import { pluralizeProjectsName } from 'components/helpers/resources/projects';

const { colors } = tailwindConfig.theme;

export default function TopRisksCard({
  dashboardFilterParams,
  isGeneratingExport,
  onExportClick,
}) {
  const currentActor = useCurrentActor();

  const { filters: cardFilters, handleFilterChange: handleCardFilterChange } =
    useFilters({ live: true, future: true, endedAndArchived: false });

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const {
    domainDashboardConfigurableFields,
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isFoldersEnabled,
    isProjectReviewDatesEnabled,
    isRiskRegisterEnabled,
  } = projectsAnalyticsDashboardContext;

  const { closeProjectsModal, openProjectsModal, projectsModal } =
    useProjectsModal({
      domainDashboardConfigurableFields,
      isApprovalsEnabled,
      isDigitalSignaturesEnabled,
      isFoldersEnabled,
      isProjectReviewDatesEnabled,
      isRiskRegisterEnabled,
    });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.TopRisksCard',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const { data: topRisksReport } = useTopRisksReportQuery({
    params: {
      filter: {
        ...Object.keys(cardFilters).reduce((acc, filter) => {
          filter.split('And').forEach((subFilter) => {
            acc[voca.lowerCase(subFilter)] = cardFilters[filter];
          });

          return acc;
        }, {}),
        ...dashboardFilterParams,
      },
    },
    queryOptions: { keepPreviousData: true },
  });

  const {
    data: domainDashboardReport,
    fetchNextPage: fetchDomainDashboardReportNextPage,
    hasNextPage: hasDomainDashboardReportNextPage,
  } = useInfiniteTopRisksReportsDashboardReportQuery({
    params: {
      filter: projectsModal.filters,
      selections: projectsModal.selections,
    },
    queryOptions: { enabled: projectsModal.isOpen },
  });

  const handleExportClick = () => {
    onExportClick({ params: { filter: projectsModal.filters } });
  };

  const handleSegmentClick = ({ entry }) => {
    openProjectsModal({
      filters: {
        activity_name: { eq: entry.name },
        top_risks_lifecycle_status: {
          in: _([])
            .tap((topRisksLifecycleStatuses) => {
              if (cardFilters.endedAndArchived)
                topRisksLifecycleStatuses.push('ended_or_archived');
              if (cardFilters.future) topRisksLifecycleStatuses.push('future');
              if (cardFilters.live) topRisksLifecycleStatuses.push('live');
            })
            .value(),
        },
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle: pluralizeProjectsName({
        count: entry.count,
        currentActor,
        shouldIncludeCount: true,
      }),
      title: `${i18nT('title')}: ${entry.name}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Top risks',
        filters: cardFilters,
        segment: entry.name,
      },
    );
  };

  const handleProjectClick = (project) => {
    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: project click',
      {
        chart: 'Top risks',
        filters: cardFilters,
        project: project.attributes.name,
      },
    );
  };

  const {
    countBreakdown = {},
    endedAndArchivedFilterCount,
    futureFilterCount,
    liveFilterCount,
  } = topRisksReport?.data.attributes || {};

  const filterCounts = {
    endedAndArchived: endedAndArchivedFilterCount,
    future: futureFilterCount,
    live: liveFilterCount,
  };

  const filterDots = {
    endedAndArchived: 'grey',
    future: 'amber',
    live: 'green',
  };

  const chartProps = useMemo(() => {
    const data = Object.keys(countBreakdown).map((risk) => ({
      count: countBreakdown[risk],
      name: risk,
    }));

    for (let i = data.length; i < 5; i++)
      data.push({ name: null, count: null });

    const appliedFilters = Object.keys(cardFilters).filter(
      (filter) => cardFilters[filter],
    );

    const totalAppliedFilterCount = appliedFilters.reduce(
      (acc, filter) => acc + filterCounts[filter],
      0,
    );

    const xAxisDomain = [0, totalAppliedFilterCount];

    return { data, xAxisDomain };
  }, [JSON.stringify(topRisksReport)]);

  const filterTranslations = {
    archived: i18nTShared('archived'),
    ended: i18nTShared('ended'),
    endedAndArchived: i18nTShared('endedAndArchived'),
    future: i18nTShared('future'),
    live: i18nTShared('live'),
  };

  return (
    <Card>
      <ReportHeader
        filters={filtersFormatter({
          filterCounts,
          filterDots,
          filters: cardFilters,
          translations: filterTranslations,
        })}
        minChecked={1}
        onFilterChange={handleCardFilterChange}
        subtitle={filtersListFormatter({
          filters: cardFilters,
          pluralRAMSName: currentActor.division.attributes.pluralRAMSName,
          translations: filterTranslations,
        })}
        title={i18nT('title')}
      />
      {topRisksReport &&
        (Object.keys(countBreakdown).length > 0 ?
          <ReportBody
            chartProps={chartProps}
            countBreakdown={countBreakdown}
            onSegmentClick={handleSegmentClick}
          />
        : <NoDataMessage message={i18nTShared('checkAppliedFilters')} />)}
      {projectsModal.isOpen && (
        <ProjectsModal
          domainDashboardReport={domainDashboardReport}
          hasNextPage={hasDomainDashboardReportNextPage}
          isGeneratingExport={isGeneratingExport}
          onClose={closeProjectsModal}
          onExportClick={handleExportClick}
          onFetchNextPage={fetchDomainDashboardReportNextPage}
          onProjectClick={handleProjectClick}
          selections={projectsModal.selections}
          subtitle={projectsModal.subtitle}
          title={projectsModal.title}
        />
      )}
    </Card>
  );
}

TopRisksCard.propTypes = {
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isGeneratingExport: PropTypes.bool.isRequired,
  onExportClick: PropTypes.func.isRequired,
};

function ReportBody({ chartProps, countBreakdown, onSegmentClick }) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.TopRisksCard',
  });

  const { handleKeyHighlight, handleKeyUnhighlight, highlightedKey } =
    useKeyHighlight(null);

  const cellFill = (key) =>
    highlightedKey && key !== highlightedKey ?
      colors.grey[100]
    : colors.amber[100];

  return (
    <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 -tw-ml-1 -tw-mr-1 tw-pb-5 tw-pl-6 tw-pr-6'>
      <BarChart
        barSize={32}
        data={chartProps.data}
        height={212}
        layout='vertical'
        margin={{ top: 0, right: 6, left: 0, bottom: 0 }}
        onMouseLeave={handleKeyUnhighlight}
        // unhighlight when over axis (event exists only when over bar chart, not when over axis)
        onMouseMove={(event) => {
          if (!event && highlightedKey) handleKeyUnhighlight();
        }}
        width={522}
      >
        <XAxis
          axisLine={false}
          domain={chartProps.xAxisDomain}
          height={32}
          tick={false}
          tickLine={{ stroke: colors.grey[400], strokeWidth: 2 }}
          type='number'
        >
          <Label
            position='insideBottom'
            style={{
              fill: colors.grey[500],
              fontFamily: 'Inter',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
            }}
            value={i18nT('chart.label', {
              interpolation: { escapeValue: false },
              pluralRAMSName: voca.capitalize(
                currentActor.division.attributes.pluralRAMSName,
              ),
            })}
          />
        </XAxis>
        <Bar background={{ fill: colors.grey['050'] }} dataKey='count'>
          {chartProps.data.map((entry) => (
            <Cell
              cursor={entry.count ? 'pointer' : 'default'}
              fill={cellFill(entry.name)}
              key={entry.name}
              onClick={() => onSegmentClick({ entry })}
              onMouseEnter={() => handleKeyHighlight(entry.name)}
            />
          ))}
          <LabelList
            content={(props) => (
              <CustomizedLabel {...props} countBreakdown={countBreakdown} />
            )}
            dataKey='name'
            position='right'
          />
        </Bar>
        <YAxis
          axisLine={{ stroke: colors.grey[400], strokeWidth: 1 }}
          tick={false}
          type='category'
          width={6}
        />
      </BarChart>
    </div>
  );
}

ReportBody.propTypes = {
  chartProps: PropTypes.shape({
    data: PropTypes.array.isRequired,
    xAxisDomain: PropTypes.array.isRequired,
  }),
  countBreakdown: PropTypes.object.isRequired,
  onSegmentClick: PropTypes.func.isRequired,
};

export function CustomizedLabel({ countBreakdown, height, value, x, y }) {
  return (
    <g className='tw-pointer-events-none'>
      <foreignObject height={height} width={510} x={x} y={y}>
        <div className='tw-flex tw-h-8 tw-items-center tw-px-3 tw-font-inter tw-text-s tw-font-medium tw-tracking-wide'>
          {value ?
            <>
              <div className='tooltip-parent tw-cursor-pointer tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-grey-700'>
                {value}
                {value.length > 64 && (
                  <Tooltip placement='top' tooltip={value} trigger='hover' />
                )}
              </div>
              <div className='tw-ml-3'>{countBreakdown[value]}</div>
            </>
          : <div className='tw-text-grey-400'>&ndash;</div>}
        </div>
      </foreignObject>
    </g>
  );
}

CustomizedLabel.propTypes = {
  countBreakdown: PropTypes.object.isRequired,
  height: PropTypes.number.isRequired,
  value: PropTypes.string,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
};
